
export class WebglHelper {

    /**
     * @function create
     * @static
     * @public
     * @returns {WebglHelper} Initialization of the class
     */
    public static create(): WebglHelper { return new WebglHelper(); }

    private constructor() { }

    /**
     * @function initShader
     * @public
     * @param {WebGL2RenderingContext} gl the context of webgl2
     * @param {number} shaderType The type of Shader to compile (vertex or fragment shader)
     * @param {string} shaderSource The Shader to compile
     * @description Compile the shader 
     * @returns {WebGLShader} Returns the compiled shader
     */
    public initShader(gl: WebGL2RenderingContext, shaderType: number, shaderSource: string): WebGLShader {
        const shader: WebGLShader = gl.createShader(shaderType);
        gl.shaderSource(shader, shaderSource);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const info = gl.getShaderInfoLog(shader);
            throw new Error(`Unable to compile the shader. \n\n ${info}`);
        }

        return shader;
    }

    /**
     * @function initProgram
     * @public
     * @param {WebGL2RenderingContext} gl the context of webgl2
     * @param {WebGLShader} vertexShader The compiled vertex shader
     * @param {WebGLShader} fragmentShader The compiled fragment shader
     * @param {WebGLBuffer} positionBuffer the buffer containing the coordinates of the canvas
     * @param {WebGLBuffer} texCoordBuffer the buffer containing the coordinates of the canvas textures
     * @description Initiates the compiled program
     * @returns {WebGLProgram} The program correctly compiled and initialized
     */
    public initProgram(gl: WebGL2RenderingContext, vertexShader: WebGLShader, fragmentShader: WebGLShader, positionBuffer: WebGLBuffer, texCoordBuffer: WebGLBuffer): WebGLProgram {
        const program: WebGLProgram = this.createProgram(gl, vertexShader, fragmentShader);

        const positionAttributeLocation = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(positionAttributeLocation);
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

        const texCoordAttributeLocation = gl.getAttribLocation(program, 'a_texCoord');
        gl.enableVertexAttribArray(texCoordAttributeLocation);
        gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
        gl.vertexAttribPointer(texCoordAttributeLocation, 2, gl.FLOAT, false, 0, 0);

        return program;
    }

    /**
     * @function createProgram
     * @public
     * @param {WebGL2RenderingContext} gl the context of webgl2
     * @param {WebGLShader} vertexShader The compiled vertex shader
     * @param {WebGLShader} fragmentShader The compiled fragment shader
     * @description Compiles the given program with the vertex and fragment shader
     * @returns {WebGLProgram} Returns the compiled program
     */
    private createProgram(gl: WebGL2RenderingContext, vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram {
        const program: WebGLProgram = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            const info = gl.getProgramInfoLog(program);
            throw new Error(`Unable to compile the WebGL program. \n\n + ${info}`);
        }

        return program;
    }


    /**
     * @function createTexture
     * @public
     * @param {WebGL2RenderingContext} gl the context of webgl2
     * @param {number} internalFormat A GLenum specifying the texture store format
     * @param {number} width A GLsizei specifying the width of the texture
     * @param {number} height A GLsizei specifying the height of the texture
     * @param {number} minFilter Paremeter for the texture magnification filter
     * @param {number} magFilter Parameter for the texture minification filter
     * @description Create a texture according to the given parameters
     * @returns {WebGLTexture} Returns the created texture
     */
    public createTexture(
        gl: WebGL2RenderingContext,
        internalFormat: number,
        width: number,
        height: number,
        minFilter: number = gl.NEAREST,
        magFilter: number = gl.NEAREST
    ): WebGLTexture {
        const texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magFilter);
        gl.texStorage2D(gl.TEXTURE_2D, 1, internalFormat, width, height);

        return texture;
    }

}
